import commonActions from '@/store/data/common/actions'
import { webRequestClient } from '@/services'
import helpers from './helpers'
import { isEmpty, toNumber, get, upperFirst } from 'lodash'
import { errorHandler } from '@/utils/helpers'

const actions = {
    retrieveDataEntry({ commit, rootState }) {
        return webRequestClient
            .get('/audience_admin/connector_creds/details/json', { params: { account_id: rootState.account.accountId } })
            .then(dataEntry => {
                if (get(dataEntry, 'status', '') === 'KO') {
                    errorHandler('Error while retrieving connections', commit)
                } else if (!isEmpty(dataEntry.list)) {
                    commit('setRetrievedItems', {
                        items: helpers.parseData(dataEntry.list, toNumber(rootState.account.accountId))
                    })
                } else {
                    commit('setRetrievedItems', { items: dataEntry.list })
                }
                commit('ui/dataEntry/setIsLoading', { isLoading: false }, { root: true })
            })
            .catch(error => {
                commit('ui/dataEntry/setIsLoading', { isLoading: false }, { root: true })
                errorHandler(error, commit)
            })
    },

    // Create connection
    createConnection({ commit, dispatch, rootState }, params) {
        //create connection
        if (params.firstCreateAttempt) {
            return webRequestClient
                .post('/audience_admin/connector_creds.json', { ...params.newConnection, account_id: toNumber(rootState.account.accountId) })
                .then(response => {
                    if (!isEmpty(response)) {
                        commit('setCreatedItem', { item: response, type: 'connection' })
                        commit('ui/dataEntry/setConnectionId', { id: response.id }, { root: true })
                        dispatch('processDataEntryConnection', { id: response.id, isCreation: true, onFinish: params.onFinish })
                    } else {
                        errorHandler('Connection creation failed', commit)
                    }
                })
                .catch(error => {
                    errorHandler(error, commit)
                })
        } else {
            const connection = { ...params.newConnection, id: rootState.ui.dataEntry.connectionId }
            dispatch('updateItem', { item: connection, dataType: 'connection', isCreation: true, onFinish: params.onFinish })
        }
    },

    processDataEntryConnection({ commit, dispatch }, params) {
        return webRequestClient
            .get(`/audience_admin/connector_creds/${params.id}.json`)
            .then(response => {
                params?.onFinish(response.status)
                if (isEmpty(response) || response.status === 'KO') {
                    //no response or functionnal error
                    if (get(response, 'data', '') && response.data.status === 'failed') {
                        commit('setUpdatedItem', { item: response.data, dataType: 'connection' })
                        errorHandler(response.data.error_msg, commit)
                    } else {
                        errorHandler('No connection for this account', commit)
                    }
                } else {
                    if (response.status && response.status === 'NOT_READY') {
                        setTimeout(() => dispatch('processDataEntryConnection', { ...params }), 500)
                    } else {
                        //status is active
                        if (params.isCreation) {
                            commit('ui/dataEntry/setConnectionId', { id: response.id }, { root: true })
                            commit('ui/dataEntry/setNextStep', {}, { root: true })
                            commit('setItemToActive', { id: response.id })
                            commit(
                                'notifications/setAddedNotification',
                                {
                                    type: 'success',
                                    message: 'Connection ' + response.label + ' has been created'
                                },
                                { root: true }
                            )
                        } else {
                            commit('ui/dataEntry/setCloseModal', {}, { root: true })
                            commit(
                                'notifications/setAddedNotification',
                                {
                                    type: 'success',
                                    // @TODO : message localization
                                    message: 'Connection ' + response.label + ' has been successfully updated'
                                },
                                { root: true }
                            )
                        }
                    }
                }
            })
            .catch(checkError => {
                errorHandler(checkError, commit)
            })
    },

    deleteItem({ commit, dispatch, rootState }, params) {
        const route =
            params.dataType === 'connection' ? `/audience_admin/connector_creds/${params.itemId}.json` : `/audience_admin/feedcrm_configs/${params.itemId}.json`

        return webRequestClient
            .delete(route, params.dataType === 'connection' ? {} : { params: { account_id: rootState.account.accountId } })
            .then(response => {
                if (response && response.status === 'deleted') {
                    //on success, retrieve all items to update store =>
                    //mutating state to avoid API call would imply too many loops to delete all config and their jobs
                    dispatch('retrieveDataEntry')
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: upperFirst(params.dataType) + ' ' + response.label + ' (id: ' + params.itemId + ') has been successfully deleted'
                        },
                        { root: true }
                    )
                } else {
                    errorHandler('Error while deleting ' + params.dataType + ' ' + params.itemId, commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    // Create configuration
    createConfiguration({ commit, dispatch }, params) {
        webRequestClient
            .post('/audience_admin/feedcrm_configs.json', params.configForm)
            .then(config => {
                if (get(config, 'status', '') !== 'KO' && !isEmpty(config)) {
                    //retrieve all connections + newly created config
                    dispatch('retrieveDataEntry')
                    commit('ui/dataEntry/setCloseModal', {}, { root: true })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Configuration ' + config.label + ' has been created'
                        },
                        { root: true }
                    )
                } else {
                    const errorMessage = 'Error during configuration creation'
                    errorHandler(errorMessage, commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    updateItem({ commit, dispatch }, params) {
        const isCreation = params.isCreation
        const _params = params.item
        const route =
            params.dataType === 'connection' ? `/audience_admin/connector_creds/${_params.id}.json` : `/audience_admin/feedcrm_configs/${_params.id}.json`
        return webRequestClient
            .put(route, params.dataType === 'connection' ? { ..._params, status: 'pending', error_msg: '' } : _params)
            .then(response => {
                if (get(response, 'status', '') !== 'KO' && !isEmpty(response)) {
                    if (params.dataType === 'connection') dispatch('processDataEntryConnection', { id: response.id, isCreation, onFinish: params.onFinish })
                    else if (params.dataType === 'configuration') {
                        commit(
                            'notifications/setAddedNotification',
                            {
                                type: 'success',
                                message: 'Configuration ' + response.label + ' has been updated'
                            },
                            { root: true }
                        )
                        dispatch('retrieveDataEntry')
                    }
                    commit('setUpdatedItem', { item: response, dataType: params.dataType })
                } else {
                    //no response or functionnal error
                    errorHandler('Error during ' + params.dataType + ' update', commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('ui/dataEntry/setIsLoading', { isLoading: false }, { root: true })
            })
    },

    addConfigurationItem({ commit }, params) {
        commit('ui/dataEntry/setFormConfiguration', { formConfiguration: params.formConfiguration }, { root: true })
        commit('ui/dataEntry/setFormAction', { action: 'create' }, { root: true })
        commit('ui/dataEntry/setStep', { step: 1 }, { root: true })
        commit('ui/dataEntry/setConnectionId', { id: params.connectionId }, { root: true })
        commit('ui/dataEntry/setDisplayModal', { display: true }, { root: true })
    }
}

export default { ...commonActions, ...actions }
